'use client'

import Link from 'next/link'
import {
  CopyCheck,
  Cuboid,
  FileCheck,
  Files,
  Globe,
  MapPin,
  MessagesSquare,
  Move,
  Smartphone,
  type LucideIcon,
} from 'lucide-react'
import React, { useCallback, useState } from 'react'

import { containerClassName } from '@/components/Container'
import { Button } from '@/components/ui/button'
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { localizePath } from '@/lib/i18n/config'
import { useLocale } from '@/lib/i18n/useLocale'
import { useMessages } from '@/lib/i18n/useMessages'
import { cn } from '@/utilities/ui'

type StepIconName =
  | 'copy-check'
  | 'cuboid'
  | 'file-check'
  | 'files'
  | 'globe'
  | 'map-pin'
  | 'messages-square'
  | 'move'
  | 'smartphone'

const stepIcons: Record<StepIconName, LucideIcon> = {
  'copy-check': CopyCheck,
  cuboid: Cuboid,
  'file-check': FileCheck,
  files: Files,
  globe: Globe,
  'map-pin': MapPin,
  'messages-square': MessagesSquare,
  move: Move,
  smartphone: Smartphone,
}

const stepBadgeStyles = [
  'bg-[#B4F0DD]',
  'bg-[#FFED8F]',
  'bg-[#8DC8FF]',
  'bg-[#CCF7FF]',
] as const

const calloutAccentStyles = [
  'text-[#FFED8F]',
  'text-[#8DC8FF]',
  'text-[#CCF7FF]',
] as const

const bookingStepsCarouselControlClassName =
  'static top-auto size-10 translate-none rounded-full border-[1.5px] border-white/95 bg-black/50 text-white hover:bg-black/60 hover:text-white disabled:opacity-40'

const bookingStepsSlideClassName =
  'w-full basis-[calc((100%-1.25rem)/1.25)] pl-5 md:basis-[calc((100%-2.5rem)/2.25)] lg:basis-[calc((100%-3.75rem)/3.25)]'

const bookingStepsCarouselInsetClassName =
  'pl-4 lg:pl-[max(1rem,calc((100vw-105rem)/2+1rem))]'

const bookingStepsLastSlideClassName =
  'mr-5 lg:mr-[max(1rem,calc((100vw-105rem)/2+1rem))]'

const bookingStepsDesktopScrollMediaQuery = '(min-width: 64rem)'

function isDesktopBookingStepsScroll(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia(bookingStepsDesktopScrollMediaQuery).matches
  )
}

function asStepIcon(icon: string): StepIconName {
  return icon as StepIconName
}

type FeaturePillProps = {
  icon: StepIconName
  label: string
}

function FeaturePill({ icon, label }: FeaturePillProps) {
  const Icon = stepIcons[icon]

  return (
    <span className="inline-flex items-center gap-1 rounded-full border-[1.5px] border-white/10 bg-white/10 px-2 py-1.5">
      <Icon aria-hidden className="size-3 shrink-0 text-white/80" />
      <span className="font-outfit text-xs font-medium leading-5 text-white/80">{label}</span>
    </span>
  )
}

type StepCalloutProps = {
  accentClassName: string
  body: string
  icon: StepIconName
  title: string
}

function StepCallout({ accentClassName, body, icon, title }: StepCalloutProps) {
  const Icon = stepIcons[icon]

  return (
    <div className="flex gap-2">
      <Icon aria-hidden className="mt-0.5 size-5 shrink-0 text-white/80" />
      <p className="font-outfit text-base leading-5 text-white/80">
        <span className={cn('font-medium', accentClassName)}>{title}</span>
        <br />
        {body}
      </p>
    </div>
  )
}

const descriptionAccentClassName = {
  blue: 'text-[#8DC8FF]',
  mint: 'text-turquoise',
  yellow: 'text-[#FFED8F]',
} as const

type DescriptionAccent = keyof typeof descriptionAccentClassName

function isDescriptionAccent(value: string | undefined): value is DescriptionAccent {
  return Boolean(value && value in descriptionAccentClassName)
}

type BookingStep = {
  callout?: {
    body: string
    icon: string
    title: string
  }
  cta?: {
    href: string
    label: string
  }
  description?: string
  descriptionAccent?: string
  descriptionParts?: Array<{
    accent?: string
    text: string
  }>
  pills?: Array<{ icon: string; label: string }>
  stepLabel: string
  title: string
}

function BookingStepDescription({ step }: { step: BookingStep }) {
  if (step.descriptionParts?.length) {
    return (
      <p className="font-outfit text-base leading-5 text-white/80">
        {step.descriptionParts.map((part, partIndex) => {
          const accentClass = isDescriptionAccent(part.accent)
            ? descriptionAccentClassName[part.accent]
            : undefined

          return accentClass ? (
            <span className={cn('font-medium', accentClass)} key={`${part.accent}-${partIndex}`}>
              {part.text}
            </span>
          ) : (
            <React.Fragment key={partIndex}>{part.text}</React.Fragment>
          )
        })}
      </p>
    )
  }

  return (
    <p className="font-outfit text-base leading-5 text-white/80">
      {step.description}
      {step.descriptionAccent ? (
        <>
          {' '}
          <span className="text-turquoise">{step.descriptionAccent}</span>
        </>
      ) : null}
    </p>
  )
}

type BookingStepCardProps = {
  index: number
  step: BookingStep
}

function BookingStepCard({ index, step }: BookingStepCardProps) {
  const locale = useLocale()
  const hasCallout = Boolean(step.callout)
  const hasPills = Boolean(step.pills?.length)
  const hasFooter = hasCallout || hasPills || Boolean(step.cta)
  const calloutAccent = calloutAccentStyles[index - 1]

  return (
    <article className="flex h-full w-full min-h-[21.75rem] flex-col gap-6 rounded-xl border-[1.5px] border-white/10 bg-white/10 p-6">
      <span
        className={cn(
          'inline-flex w-fit items-center rounded-full px-3 py-2 font-outfit text-base font-medium leading-5 text-dark-800/80',
          stepBadgeStyles[index],
        )}
      >
        {step.stepLabel}
      </span>

      <h3 className="font-display text-[1.75rem] font-medium leading-8 tracking-tight text-white">
        {step.title}
      </h3>

      <BookingStepDescription step={step} />

      {hasFooter ? (
        <div className={cn('mt-auto flex flex-col gap-6', step.cta && hasPills && 'min-h-[6.75rem] justify-between')}>
          {hasPills ? (
            <div className="flex flex-wrap gap-2 lg:gap-0.5">
              {step.pills?.map((pill) => (
                <FeaturePill key={pill.label} icon={asStepIcon(pill.icon)} label={pill.label} />
              ))}
            </div>
          ) : null}

          {hasCallout && step.callout && calloutAccent ? (
            <StepCallout
              accentClassName={calloutAccent}
              body={step.callout.body}
              icon={asStepIcon(step.callout.icon)}
              title={step.callout.title}
            />
          ) : null}

          {step.cta ? (
            <Button asChild className="w-fit">
              <Link href={localizePath(step.cta.href, locale)}>{step.cta.label}</Link>
            </Button>
          ) : null}
        </div>
      ) : null}
    </article>
  )
}

export const BookingSteps: React.FC = () => {
  const messages = useMessages()
  const copy = messages.landing.bookingSteps
  const [carouselApi, setCarouselApi] = useState<CarouselApi>()

  const handleCarouselNext = useCallback(() => {
    if (!carouselApi) {
      return
    }

    const currentSnap = carouselApi.selectedScrollSnap()
    const lastSnap = carouselApi.scrollSnapList().length - 1

    if (isDesktopBookingStepsScroll() && currentSnap === 0 && lastSnap > 0) {
      carouselApi.scrollTo(lastSnap)
      return
    }

    carouselApi.scrollNext()
  }, [carouselApi])

  const handleCarouselPrev = useCallback(() => {
    if (!carouselApi) {
      return
    }

    const currentSnap = carouselApi.selectedScrollSnap()
    const lastSnap = carouselApi.scrollSnapList().length - 1

    if (isDesktopBookingStepsScroll() && currentSnap === lastSnap && lastSnap > 0) {
      carouselApi.scrollTo(0)
      return
    }

    carouselApi.scrollPrev()
  }, [carouselApi])

  return (
    <section
      aria-labelledby="booking-steps-heading"
      className="overflow-x-clip bg-bluetext"
      id="how-it-works"
    >
      <Carousel
        aria-label={copy.carouselAriaLabel}
        className="w-full"
        opts={{ align: 'start', containScroll: 'trimSnaps' }}
        setApi={setCarouselApi}
      >
        <div className={cn(containerClassName, 'pb-0 pt-12 lg:pt-20')}>
          <div className="mb-10 flex flex-col gap-6 lg:mb-12 lg:flex-row lg:items-end lg:justify-between lg:gap-10">
            <h2
              id="booking-steps-heading"
              className="w-full font-display text-[2.5rem] font-semibold leading-tight tracking-tight text-white md:text-[3.5rem] lg:text-[3.75rem] lg:leading-none"
            >
              <span className="block">{copy.titleLine1}</span>
              <span className="block text-turquoise">{copy.titleLine2}</span>
            </h2>

            <p className="max-w-md pb-1 font-outfit text-base leading-5 text-white/80 lg:text-right">
              {copy.intro}
            </p>
          </div>
        </div>

        <div
          className={cn(
            'booking-steps-carousel relative ml-[calc(50%-50vw)] w-screen max-w-[100vw]',
            bookingStepsCarouselInsetClassName,
          )}
          data-booking-steps-carousel
        >
          <CarouselContent className="-ml-5" role="list">
            {copy.steps.map((step, index) => (
              <CarouselItem
                key={step.title}
                className={cn(
                  bookingStepsSlideClassName,
                  index === copy.steps.length - 1 && bookingStepsLastSlideClassName,
                )}
                role="listitem"
              >
                <BookingStepCard index={index} step={step} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </div>

        <div className={cn(containerClassName, 'pb-12 pt-6 lg:pb-20')}>
          <div className="flex items-center justify-end gap-3">
            <CarouselPrevious
              aria-label={copy.carouselPrevious}
              className={bookingStepsCarouselControlClassName}
              onClick={handleCarouselPrev}
              size="icon"
              variant="outline"
            />
            <CarouselNext
              aria-label={copy.carouselNext}
              className={bookingStepsCarouselControlClassName}
              onClick={handleCarouselNext}
              size="icon"
              variant="outline"
            />
          </div>
        </div>
      </Carousel>
    </section>
  )
}
